import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";

const OP_LABELS = {
  eq: "É igual a", neq: "É diferente de",
  contains: "Contém", not_contains: "Não contém",
  starts_with: "Inicia com", not_starts_with: "Não inicia com",
  ends_with: "Termina com",
  empty: "Está vazio", not_empty: "Não está vazio",
  in: "É qualquer um de", not_in: "Não é nenhum de",
  gt: "Maior que", gte: "Maior ou igual", lt: "Menor que", lte: "Menor ou igual",
  between: "Entre",
  before: "Antes de", after: "Depois de", last_30_days: "Últimos 30 dias",
};
const NEEDS_VALUE_SINGLE = ["eq","neq","contains","not_contains","ends_with","gt","gte","lt","lte","before","after"];
const NEEDS_MULTI = ["starts_with","not_starts_with","in","not_in","between"];
const NEEDS_NONE  = ["empty","not_empty","last_30_days"];

export function FilterGroup({ node, onChange, fields, depth = 0 }) {
  const setOp = (op) => onChange({ ...node, op });
  const addCond = () => onChange({
    ...node,
    children: [...node.children, { field: fields[0]?.key || "", op: "eq", value: "" }],
  });
  const addGroup = () => onChange({
    ...node,
    children: [...node.children, { op: "AND", children: [] }],
  });
  const updateChild = (i, c) => onChange({
    ...node,
    children: node.children.map((x, idx) => (idx === i ? c : x)),
  });
  const removeChild = (i) => onChange({
    ...node,
    children: node.children.filter((_, idx) => idx !== i),
  });

  const wrapCls = depth > 0
    ? "rounded-md border border-slate-200 bg-slate-50 p-2 space-y-2"
    : "rounded-md p-2 space-y-2";
  return (
    <div className={wrapCls}>
      <div className="flex items-center gap-2">
        <Select value={node.op} onValueChange={setOp}>
          <SelectTrigger className="h-7 w-20 text-[11px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="AND">E</SelectItem>
            <SelectItem value="OR">OU</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="ghost" onClick={addCond} className="h-7 text-[11px]">
          <Plus className="h-3 w-3 mr-1" /> Condição
        </Button>
        <Button size="sm" variant="ghost" onClick={addGroup} className="h-7 text-[11px]">
          <Plus className="h-3 w-3 mr-1" /> Grupo
        </Button>
      </div>
      {node.children.map((c, i) => (
        <div key={i} className="flex items-start gap-1">
          <div className="flex-1">
            {c.children !== undefined ? (
              React.createElement(FilterGroup, {
                node: c,
                onChange: (v) => updateChild(i, v),
                fields: fields,
                depth: depth + 1,
              })
            ) : (
              <ConditionRow cond={c} onChange={(v) => updateChild(i, v)} fields={fields} />
            )}
          </div>
          <Button size="sm" variant="ghost" onClick={() => removeChild(i)} className="h-7 w-7 p-0 text-rose-500">
            <X className="h-3 w-3" />
          </Button>
        </div>
      ))}
    </div>
  );
}

export function ConditionRow({ cond, onChange, fields }) {
  const field = fields.find((f) => f.key === cond.field);
  const ops = field?.allowed_operators || [];
  const filterables = fields.filter((f) => f.filterable);
  const showSingle = NEEDS_VALUE_SINGLE.includes(cond.op);
  const showMulti  = NEEDS_MULTI.includes(cond.op);
  const showNone   = NEEDS_NONE.includes(cond.op);
  return (
    <div className="flex flex-wrap gap-1 items-center">
      <Select
        value={cond.field}
        onValueChange={(v) => onChange({ field: v, op: "eq", value: "" })}
      >
        <SelectTrigger className="h-7 w-[180px] text-[11px]">
          <SelectValue placeholder="Campo" />
        </SelectTrigger>
        <SelectContent>
          {filterables.map((f) => (
            <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={cond.op}
        onValueChange={(v) => onChange({ ...cond, op: v, value: "", values: [] })}
      >
        <SelectTrigger className="h-7 w-[150px] text-[11px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {ops.map((o) => <SelectItem key={o} value={o}>{OP_LABELS[o]}</SelectItem>)}
        </SelectContent>
      </Select>
      {showSingle && (
        <Input
          value={cond.value ?? ""}
          onChange={(e) => onChange({ ...cond, value: e.target.value })}
          className="h-7 text-[11px] w-[180px]"
          placeholder="valor"
        />
      )}
      {showMulti && (
        <Input
          value={(cond.values || []).join(", ")}
          onChange={(e) => {
            const arr = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
            onChange({ ...cond, values: arr });
          }}
          className="h-7 text-[11px] w-[240px]"
          placeholder="valor1, valor2, valor3..."
        />
      )}
      {showNone && (
        <span className="text-[10.5px] text-slate-400 italic">sem valor</span>
      )}
    </div>
  );
}
